import { clsx } from 'clsx'
import { unsafeCSS } from 'lit'
import { property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { CSSFlexDirection, Spacing } from '../../common/types'
import { DdsElement } from '../../internal/dds-hu-element'
import styles from './stack.scss?inline'

/**
 * `dap-ds-stack`
 * @summary A stack is a container for stacked content.
 * @element dap-ds-stack
 * @title - Stack
 *
 * @slot - The content of the stack.
 *
 * @csspart stack - The main stack container.
 */
export default class DapDSStack extends DdsElement {
  static tagName = 'dap-ds-stack'

  /** The direction of the stack.
   * @type {'column' | 'column-reverse' | 'row' | 'row-reverse'}
   */
  @property() direction: CSSFlexDirection = 'column'
  /** Adds margin to the end of the stack. */
  @property({ type: Boolean }) endMargin?: boolean = false
  /** Adds margin to the start of the stack. */
  @property({ type: Boolean }) startMargin?: boolean = false

  private _spacing: Spacing
  /** The spacing of the stack. Uses the system spacing scale (100, 200, 300...etc) */
  @property({ type: Number })
  set spacing(value: Spacing) {
    this._spacing = value
  }
  get spacing() {
    if (
      !this._spacing &&
      this._spacing !== 0 &&
      (this.direction === 'column' || this.direction === 'column-reverse')
    ) {
      return 600
    }

    if (
      !this._spacing &&
      this._spacing !== 0 &&
      (this.direction === 'row' || this.direction === 'row-reverse')
    ) {
      return 200
    }

    return this._spacing
  }

  static readonly styles = unsafeCSS(styles)

  getMarginStyles(
    direction: CSSFlexDirection,
    spacing: number,
    startMargin?: boolean,
    endMargin?: boolean,
  ) {
    if (direction === 'row' || direction === 'row-reverse') {
      return `margin-left: ${startMargin ? `var(--dds-spacing-${spacing})` : '0'}; margin-right: ${endMargin ? `var(--dds-spacing-${spacing})` : '0'};`
    }

    return `margin-top: ${startMargin ? `var(--dds-spacing-${spacing})` : '0'}; margin-bottom: ${endMargin ? `var(--dds-spacing-${spacing})` : '0'};`
  }

  render() {
    return html`
      <div
        class=${clsx('stack', `stack-gap-${this.spacing}`, {
          [`stack-direction-${this.direction}`]: this.direction,
        })}
        part="base"
        style=${ifDefined(
          this.getMarginStyles(
            this.direction,
            this.spacing,
            this.startMargin,
            this.endMargin,
          ),
        )}>
        <slot></slot>
      </div>
    `
  }
}
